import { useState } from "react";
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { FileDTO, FileItem } from "@titancloud/shared";
import * as trashApi from "../../api/trash.api";
import { TrashToolbar } from "./TrashToolbar";
import { TrashRow } from "./TrashRow";
import { TrashListSkeleton } from "../ui/Skeleton";
import { Button } from "../ui/Button";
import { PreviewModal } from "../preview/PreviewModal";

interface BreadcrumbEntry {
  id: string;
  name: string;
}

const TRASH_PAGE_SIZE = 50;

function toFileItem(f: FileDTO): FileItem {
  return { ...f, kind: "file" };
}

export default function TrashView() {
  const qc = useQueryClient();
  const [path, setPath] = useState<BreadcrumbEntry[]>([]);
  const [actionItemId, setActionItemId] = useState<string | null>(null);
  const [actionType, setActionType] = useState<"restore" | "delete" | null>(null);
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);

  const currentFolderId = path.length > 0 ? path[path.length - 1]!.id : null;

  const rootQuery = useInfiniteQuery({
    queryKey: ["trash"],
    queryFn: ({ pageParam }) =>
      trashApi.listTrash({ limit: TRASH_PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (_lastPage, allPages) => {
      const loadedFiles = allPages.reduce((sum, p) => sum + p.files.length, 0);
      const loadedFolders = allPages.reduce((sum, p) => sum + p.folders.length, 0);
      const totalFiles = allPages[0]?.totalFiles ?? 0;
      const totalFolders = allPages[0]?.totalRootFolders ?? 0;
      return loadedFiles < totalFiles || loadedFolders < totalFolders
        ? allPages.length * TRASH_PAGE_SIZE
        : undefined;
    },
    enabled: currentFolderId === null,
  });

  const rootFiles = rootQuery.data?.pages.flatMap((p) => p.files) ?? [];
  const rootFolders = rootQuery.data?.pages.flatMap((p) => p.folders) ?? [];

  const folderQuery = useQuery({
    queryKey: ["trash", "folder", currentFolderId],
    queryFn: () => trashApi.listTrashFolder(currentFolderId!),
    enabled: currentFolderId !== null,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["trash"] });

  const restoreMutation = useMutation({
    mutationFn: ({ id, type }: { id: string; type: "file" | "folder" }) =>
      trashApi.restoreItem(id, type),
    onSuccess: (_data, vars) => {
      invalidate();
      // Restored folders and files reappear in the cached folder tree and its item counts.
      void qc.invalidateQueries({ queryKey: ["folders"] });
      setPath([]);
      setActionItemId(null);
      toast.success(vars.type === "folder" ? "Folder restored" : "File restored");
    },
    onError: () => {
      setActionItemId(null);
      toast.error("Restore failed");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: ({ id, type }: { id: string; type: "file" | "folder" }) =>
      trashApi.permanentDeleteItem(id, type),
    onSuccess: () => {
      invalidate();
      setActionItemId(null);
      toast.success("Permanently deleted");
    },
    onError: () => {
      setActionItemId(null);
      toast.error("Delete failed");
    },
  });

  const emptyMutation = useMutation({
    mutationFn: trashApi.emptyTrash,
    onSuccess: () => {
      invalidate();
      setPath([]);
      toast.success("Trash emptied");
    },
    onError: () => toast.error("Failed to empty trash"),
  });

  const handleRestore = (id: string, type: "file" | "folder") => {
    setActionItemId(id);
    setActionType("restore");
    restoreMutation.mutate({ id, type });
  };

  const handleDelete = (id: string, type: "file" | "folder") => {
    setActionItemId(id);
    setActionType("delete");
    deleteMutation.mutate({ id, type });
  };

  const isLoading = currentFolderId === null ? rootQuery.isLoading : folderQuery.isLoading;
  const isAtRoot = currentFolderId === null;

  const files = isAtRoot ? rootFiles : (folderQuery.data?.files ?? []);
  const folders = isAtRoot ? rootFolders : (folderQuery.data?.folders ?? []);
  const totalItems = files.length + folders.length;
  const totalRootItems =
    (rootQuery.data?.pages[0]?.totalFiles ?? 0) + (rootQuery.data?.pages[0]?.totalRootFolders ?? 0);

  const fileItems: FileItem[] = files.map(toFileItem);
  const openPreview = (fileId: string) => {
    const idx = fileItems.findIndex((f) => f.id === fileId);
    if (idx !== -1) setPreviewIndex(idx);
  };

  const navigateInto = (id: string, name: string) =>
    setPath((p) => [...p, { id, name }]);

  const navigateTo = (index: number) =>
    index === -1 ? setPath([]) : setPath((p) => p.slice(0, index + 1));

  return (
    <div className="flex h-full flex-col">
      <TrashToolbar
        itemCount={totalRootItems}
        onEmptyTrash={() => emptyMutation.mutate()}
        isEmptying={emptyMutation.isPending}
      />

      {/* Breadcrumb */}
      {path.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 border-b border-gray-800 bg-gray-900/50 px-4 py-2 text-xs">
          <button
            onClick={() => navigateTo(-1)}
            className="text-gray-400 hover:text-gray-200 transition-colors"
          >
            Trash
          </button>
          {path.map((entry, i) => (
            <span key={entry.id} className="flex items-center gap-1">
              <span className="text-gray-700">/</span>
              <button
                onClick={() => navigateTo(i)}
                className={`transition-colors hover:text-gray-200 ${
                  i === path.length - 1 ? "text-gray-200 font-medium" : "text-gray-400"
                }`}
              >
                {entry.name}
              </button>
            </span>
          ))}
          <span className="ml-auto text-gray-600 italic">
            Restore the parent folder to recover these files
          </span>
        </div>
      )}

      {isLoading ? (
        <TrashListSkeleton />
      ) : totalItems === 0 && isAtRoot ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 text-gray-500">
          <svg className="h-12 w-12" viewBox="0 0 48 48" fill="none">
            <path d="M16 8h16l4 8H12l4-8zM8 16v24a4 4 0 004 4h24a4 4 0 004-4V16H8z" stroke="currentColor" strokeWidth="2" />
          </svg>
          <p className="text-sm">Trash is empty</p>
          <p className="text-xs">Deleted files are kept for 30 days</p>
        </div>
      ) : (
        <div className="flex-1 overflow-auto">
          <div className="flex items-center border-b border-gray-800 px-4 py-2 text-xs font-medium text-gray-500">
            <div className="flex-1">Name</div>
            <div className="hidden w-36 sm:block">Deleted</div>
            <div className="w-36 text-right">Actions</div>
          </div>

          {totalItems === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-16 text-gray-600">
              <p className="text-sm">This folder is empty</p>
            </div>
          ) : (
            <>
              {folders.map((folder) => {
                const isThisRestoring = actionItemId === folder.id && actionType === "restore" && restoreMutation.isPending;
                const isThisDeleting = actionItemId === folder.id && actionType === "delete" && deleteMutation.isPending;
                return (
                  <TrashRow
                    key={folder.id}
                    name={folder.name}
                    kind="folder"
                    deletedAt={folder.deletedAt ?? ""}
                    isAtRoot={isAtRoot}
                    onNavigate={() => navigateInto(folder.id, folder.name)}
                    {...(isAtRoot ? { onRestore: () => handleRestore(folder.id, "folder") } : {})}
                    onDelete={() => handleDelete(folder.id, "folder")}
                    isRestoring={isThisRestoring}
                    isDeleting={isThisDeleting}
                  />
                );
              })}

              {files.map((file) => {
                const isThisRestoring = actionItemId === file.id && actionType === "restore" && restoreMutation.isPending;
                const isThisDeleting = actionItemId === file.id && actionType === "delete" && deleteMutation.isPending;
                return (
                  <TrashRow
                    key={file.id}
                    name={file.name}
                    kind="file"
                    deletedAt={file.deletedAt ?? ""}
                    isAtRoot={isAtRoot}
                    mimeType={file.mimeType}
                    extension={file.extension}
                    thumbnailUrl={file.thumbnailUrl}
                    onOpen={() => openPreview(file.id)}
                    {...(isAtRoot ? { onRestore: () => handleRestore(file.id, "file") } : {})}
                    onDelete={() => handleDelete(file.id, "file")}
                    isRestoring={isThisRestoring}
                    isDeleting={isThisDeleting}
                  />
                );
              })}
            </>
          )}

          {isAtRoot && rootQuery.hasNextPage && (
            <div className="flex justify-center py-4">
              <Button
                variant="ghost"
                onClick={() => rootQuery.fetchNextPage()}
                disabled={rootQuery.isFetchingNextPage}
              >
                {rootQuery.isFetchingNextPage ? "Loading…" : "Load more"}
              </Button>
            </div>
          )}
        </div>
      )}

      {previewIndex !== null && fileItems.length > 0 && (
        <PreviewModal
          files={fileItems}
          initialIndex={previewIndex}
          onClose={() => setPreviewIndex(null)}
          trash
        />
      )}
    </div>
  );
}
